import type { SoundPlacement, PlacementKind, Variation, Mod, Drop } from './soundscape';
import type { AudioCue } from './cues';
import { newCueId, sanitizeCueState } from './cues';
import type { AudioChannel, SpatialType } from './audioManager';
import type { StateTag } from '../scene/tagging';

// ── The unified soundscape document ────────────────────────────────────────
//
// Stage 2 merges the two Stage-1 sources — per-era beds + music (was
// states.json) and time-of-day one-shots (was audio-cues.json) — into ONE
// authored file: public/soundscape.json. Both surfaces converge on the same
// vocabulary:
//
//   placements[]  the continuous layer: BED loops (era-gated, day-windowed,
//                 spatial) + the MUSIC slot defaults per era. Authored on the
//                 timeline's AMBIENCE lane.
//   cues[]        the instantaneous layer: one-shots fired as the playhead
//                 crosses a time of day. Authored on the CUES lane.
//
// The SoundscapeEngine reads `placements` + `cues` every frame; the timeline +
// inspector mutate them in place, so edits are live. Saving serializes the
// whole doc back to soundscape.json.
//
// version lets the loader migrate older shapes; 2 = first unified file.
export interface SoundscapeDoc {
  version: number;
  placements: SoundPlacement[];
  cues: AudioCue[];
  /** SHA-256 of the exact bytes this doc was loaded from — load-provenance, NOT
   *  persisted (the formatter ignores it). Seeds the save freshness guard (S1):
   *  the saver sends it as If-Match so a write refuses (409) if the file changed
   *  on disk since load. Undefined when loaded from the legacy fallback. */
  baseHash?: string;
  /** Raw entries that FAILED validation at load — kept verbatim and re-emitted
   *  on save so a typo'd or forward-compat entry is warned, NOT silently deleted
   *  (S2). The engine never sees them (they're not functional until fixed); they
   *  ride through the save formatter untouched. */
  dropped?: { placements: unknown[]; cues: unknown[] };
}

// The keys each validator knows about. Anything else on an entry is an unknown
// field — preserved through load + save (forward-compat / hand annotations)
// rather than silently stripped (S2). Exported so the save formatter emits the
// same unknown keys it must round-trip.
export const PLACEMENT_KEYS = new Set([
  'id', 'kind', 'asset', 'scope', 'channel', 'at', 'spatial', 'refDistance', 'maxDistance', 'gain', 'span', 'loop', 'vary', 'mod', 'drops', 'minSpacingSec',
  't', 'dur', 'in', 'out', 'fadeIn', 'fadeOut', 'resume', 'playback', 'arbitration', 'requiresPower',
]);
export const CUE_KEYS = new Set(['id', 't', 'state', 'label', 'actions', 'vary']);

/** Best-effort id for a drop warning — the entry's id, else the raw value. */
const entryId = (raw: unknown): unknown =>
  raw && typeof raw === 'object' && 'id' in raw ? (raw as { id: unknown }).id : raw;

/** Copy any keys not in `known` from raw onto the validated entry, so a save
 *  round-trips unknown fields instead of dropping them (S2). */
function preserveUnknown(raw: object, out: object, known: Set<string>): void {
  for (const k of Object.keys(raw)) {
    if (!known.has(k)) (out as Record<string, unknown>)[k] = (raw as Record<string, unknown>)[k];
  }
}

/** Keep the FIRST entry per id, warn + drop later duplicates (S8). Two entries
 *  sharing an id fight one engine map slot — with differing routes that's
 *  stop/start churn spawning overlapping fading instances. First-wins matches
 *  the music slot's `.find()` semantics, so which one wins doesn't change. */
function dedupeById<T extends { id: string }>(items: T[], kind: string): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const it of items) {
    if (seen.has(it.id)) {
      console.warn(`[soundscape] duplicate ${kind} id "${it.id}" — keeping the first, dropping this one`);
      continue;
    }
    seen.add(it.id);
    out.push(it);
  }
  return out;
}

export const SOUNDSCAPE_VERSION = 2;

const PLACEMENT_KINDS: PlacementKind[] = ['bed', 'music', 'clip'];
const CHANNELS: AudioChannel[] = ['ambient', 'music', 'narration', 'sfx', 'room'];
const SPATIALS: SpatialType[] = ['point', 'area', 'global'];
const SCOPES: StateTag[] = ['past', 'present', 'both'];

const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));

function validateVariation(raw: unknown): Variation | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Partial<Variation>;
  const out: Variation = {};
  if (typeof r.chance === 'number') out.chance = clamp01(r.chance);
  if (Array.isArray(r.oneOf)) {
    const pool = r.oneOf.filter((x): x is string => typeof x === 'string');
    if (pool.length) out.oneOf = pool;
  }
  if (typeof r.detune === 'number' && r.detune > 0) out.detune = r.detune;
  if (typeof r.jitterSec === 'number' && r.jitterSec > 0) out.jitterSec = r.jitterSec;
  return Object.keys(out).length ? out : undefined;
}

// A12 — drops validation: keep well-formed members, warn + drop the rest. An
// invalid `every` (the one field that can wedge the scheduler) invalidates the
// member; soft fields clamp or fall away.
function validateDrops(raw: unknown, placementId: string): Drop[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: Drop[] = [];
  raw.forEach((r, i) => {
    const d = r as Partial<Drop>;
    const pool = Array.isArray(d.oneOf) ? d.oneOf.filter((x): x is string => typeof x === 'string') : [];
    const every = Array.isArray(d.every) && d.every.length === 2 ? [Number(d.every[0]), Number(d.every[1])] as [number, number] : null;
    if (!pool.length || !every || !Number.isFinite(every[0]) || !Number.isFinite(every[1]) || every[0] < 0 || every[0] > every[1]) {
      console.warn(`[soundscape] bed "${placementId}" drop #${i} invalid (needs oneOf[] + every:[min<=max]) — skipped`);
      return;
    }
    const o: Drop = { oneOf: pool, every };
    if (typeof d.chance === 'number') o.chance = clamp01(d.chance);
    if (typeof d.detune === 'number' && d.detune > 0) o.detune = d.detune;
    if (typeof d.gain === 'number') o.gain = clamp01(d.gain);
    else if (Array.isArray(d.gain) && d.gain.length === 2) o.gain = [clamp01(Number(d.gain[0])), clamp01(Number(d.gain[1]))];
    if (d.at && typeof d.at === 'object') {
      const at: Drop['at'] = {};
      if (typeof d.at.heroId === 'string') at.heroId = d.at.heroId;
      if (Array.isArray(d.at.position) && d.at.position.length === 3) at.position = [Number(d.at.position[0]), Number(d.at.position[1]), Number(d.at.position[2])];
      if (at.heroId || at.position) o.at = at;
    }
    out.push(o);
  });
  return out.length ? out : undefined;
}

function validateMod(raw: unknown): Mod | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Partial<Mod>;
  const out: Mod = {};
  if (typeof r.depth === 'number' && r.depth > 0) out.depth = clamp01(r.depth);
  if (typeof r.rateSec === 'number' && r.rateSec > 0) out.rateSec = r.rateSec;
  if (typeof r.reshuffleSec === 'number' && r.reshuffleSec > 0) out.reshuffleSec = r.reshuffleSec;
  return Object.keys(out).length ? out : undefined;
}

/** A fresh bed-placement id. Browser context, so Math.random is fine (unlike
 *  workflow scripts). Beds authored on the timeline get one of these; the
 *  Stage-1 migration keeps its derived `bed_<era>_<hero>` ids. */
export function newBedId(): string {
  return 'bed_' + Math.random().toString(36).slice(2, 9);
}

// ── validation (defensive; the file is hand- + tool-edited) ─────────────────
function validatePlacement(raw: unknown): SoundPlacement | null {
  const p = raw as Partial<SoundPlacement>;
  if (typeof p.id !== 'string') return null;
  if (!PLACEMENT_KINDS.includes(p.kind as PlacementKind)) return null;
  // A12: a bed may be a pure drop population (no base asset); music always
  // needs its asset. Validate drops early so the asset requirement can see them.
  const drops = p.kind === 'bed' ? validateDrops(p.drops, p.id) : undefined;
  const hasAsset = typeof p.asset === 'string';
  if (!hasAsset && !(p.kind === 'bed' && drops?.length)) return null;
  // N1: a clip is a REGION — it needs a position and a real-seconds length.
  if (p.kind === 'clip' && (typeof p.t !== 'number' || typeof p.dur !== 'number' || p.dur <= 0)) {
    console.warn(`[soundscape] clip "${p.id}" needs numeric t (day 0..1) + dur > 0 (seconds) — invalid`);
    return null;
  }
  const scope: StateTag = SCOPES.includes(p.scope as StateTag) ? (p.scope as StateTag) : 'both';
  const channel: AudioChannel = CHANNELS.includes(p.channel as AudioChannel)
    ? (p.channel as AudioChannel)
    : (p.kind === 'music' ? 'music' : 'ambient');
  const out: SoundPlacement = { id: p.id, kind: p.kind as PlacementKind, scope, channel };
  if (hasAsset) out.asset = p.asset;
  if (drops) out.drops = drops;
  if (typeof p.minSpacingSec === 'number' && p.minSpacingSec > 0) out.minSpacingSec = p.minSpacingSec;
  if (p.resume === 'restart' || p.resume === 'continue') out.resume = p.resume; // N4 (music)
  // N1 — clip region + trim fields. `in`/`out` define the source window; a
  // backwards window (out <= in) is warned and the `out` dropped (plays to end).
  if (p.kind === 'clip') {
    out.t = clamp01(Number(p.t));
    out.dur = Number(p.dur);
    // N10 — playback mode. Default 'sync' (the N1 region) when absent; only the
    // two known values are honoured (a typo falls back to 'sync', not silent).
    if (p.playback === 'trigger') out.playback = 'trigger';
    else if (p.playback === 'sync') out.playback = 'sync';
    // N7 — arbitration mode (default 'force' when absent; typo → dropped).
    if (p.arbitration === 'defer' || p.arbitration === 'duck' || p.arbitration === 'skip' || p.arbitration === 'force') out.arbitration = p.arbitration;
    if (typeof p.in === 'number' && p.in >= 0) out.in = p.in;
    if (typeof p.out === 'number' && p.out > 0) {
      if (p.out <= (out.in ?? 0)) console.warn(`[soundscape] clip "${p.id}" out (${p.out}) <= in (${out.in ?? 0}) — out dropped`);
      else out.out = p.out;
    }
    if (typeof p.fadeIn === 'number' && p.fadeIn >= 0) out.fadeIn = p.fadeIn;
    if (typeof p.fadeOut === 'number' && p.fadeOut >= 0) out.fadeOut = p.fadeOut;
  }
  if (p.requiresPower === true) out.requiresPower = true; // N8 — strictly boolean true
  if (p.at && typeof p.at === 'object') {
    const at: SoundPlacement['at'] = {};
    if (typeof p.at.heroId === 'string') at.heroId = p.at.heroId;
    if (Array.isArray(p.at.position) && p.at.position.length === 3) {
      at.position = [Number(p.at.position[0]), Number(p.at.position[1]), Number(p.at.position[2])];
    }
    if (at.heroId || at.position) out.at = at;
  }
  if (SPATIALS.includes(p.spatial as SpatialType)) out.spatial = p.spatial as SpatialType;
  if (typeof p.refDistance === 'number' && p.refDistance > 0) out.refDistance = p.refDistance;
  if (typeof p.maxDistance === 'number' && p.maxDistance > 0) out.maxDistance = p.maxDistance;
  if (typeof p.gain === 'number') out.gain = clamp01(p.gain);
  if (Array.isArray(p.span) && p.span.length === 2) {
    out.span = [clamp01(Number(p.span[0])), clamp01(Number(p.span[1]))];
  }
  if (typeof p.loop === 'boolean') out.loop = p.loop;
  const vary = validateVariation(p.vary);
  if (vary) out.vary = vary;
  const mod = validateMod(p.mod);
  if (mod) out.mod = mod;
  preserveUnknown(p as object, out, PLACEMENT_KEYS);
  return out;
}

function validateCue(raw: unknown): AudioCue | null {
  const c = raw as Partial<AudioCue>;
  if (typeof c.t !== 'number' || !Array.isArray(c.actions)) return null;
  const id = typeof c.id === 'string' ? c.id : newCueId();
  const out: AudioCue = {
    id,
    t: ((c.t % 1) + 1) % 1,
    state: sanitizeCueState(c.state, id),
    label: c.label,
    actions: c.actions,
    vary: validateVariation(c.vary),
  };
  preserveUnknown(c as object, out, CUE_KEYS);
  return out;
}

// N10 — placeholder bar length (real seconds) for a migrated trigger clip. The
// engine ignores dur for triggers (they play their source out fire-and-forget);
// this is only the bar width until the UI refines it from the asset's duration.
const DEFAULT_TRIGGER_DUR = 2;

/** N10 — migrate a legacy cue into a `trigger` clip. A cue in practice played
 *  ONE sound (its first audio.play action); the merge keeps that and retires the
 *  unused "a cue runs arbitrary actions" power. A cue with no audio.play action
 *  can't become a sound clip → warned + dropped. id/era/anchor/volume/vary carry
 *  over; the custom label rides as a preserved-unknown field (round-trips). */
function cueToTriggerClip(cue: AudioCue): SoundPlacement | null {
  const play = cue.actions.find((a) => a.kind === 'audio.play') as
    | { kind: 'audio.play'; id?: string; channel?: AudioChannel; volume?: number; at?: { heroId?: string; position?: [number, number, number] } }
    | undefined;
  if (!play || typeof play.id !== 'string') {
    console.warn(`[soundscape] cue "${cue.id}" has no audio.play action — dropped in the N10 merge (the arbitrary-actions cue power is retired)`);
    return null;
  }
  const out: SoundPlacement = {
    id: cue.id, kind: 'clip', playback: 'trigger',
    asset: play.id, scope: cue.state ?? 'both', channel: play.channel ?? 'sfx',
    t: cue.t, dur: DEFAULT_TRIGGER_DUR,
  };
  if (play.at && (play.at.heroId || play.at.position)) {
    out.at = {};
    if (play.at.heroId) out.at.heroId = play.at.heroId;
    if (play.at.position) out.at.position = play.at.position;
  }
  if (typeof play.volume === 'number') out.gain = clamp01(play.volume);
  if (cue.vary) out.vary = cue.vary;
  if (cue.label) (out as unknown as Record<string, unknown>).label = cue.label; // preserved-unknown → bar label + round-trips
  return out;
}

/** N10 — fold valid cues into placements as trigger clips and return an empty
 *  cues[] (the citizen is retired). Quarantined-invalid cues are NOT touched
 *  here — they ride in doc.dropped.cues and re-emit verbatim on save (S2). */
function migrateCues(placements: SoundPlacement[], cues: AudioCue[]): { placements: SoundPlacement[]; cues: AudioCue[] } {
  const migrated = cues.map(cueToTriggerClip).filter((c): c is SoundPlacement => c !== null);
  return { placements: dedupeById([...placements, ...migrated], 'placement'), cues: [] };
}

export type SoundscapeFallback = () => Promise<{ placements: SoundPlacement[]; cues: AudioCue[] }>;

/** SHA-256 hex of `text`'s utf8 bytes — matches the dev writer's node-side
 *  digest, so the saver can prove the disk file is unchanged since load (S1).
 *  Defensive: returns undefined (guard simply inactive) if SubtleCrypto is
 *  unavailable, so a load never fails on the hash. localhost is a secure
 *  context, so this resolves in normal dev. */
async function sha256Hex(text: string): Promise<string | undefined> {
  try {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
    return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return undefined;
  }
}

/** Load the unified soundscape. If soundscape.json is present it's the source
 *  of truth; if it's missing (fresh checkout from the Stage-1 era, or a hand
 *  delete) the `fallback` derives the same data from the legacy states.json +
 *  audio-cues.json so nothing authored is lost. Returns plain arrays the engine
 *  + timeline mutate in place. */
export async function loadSoundscape(
  url = '/soundscape.json',
  fallback?: SoundscapeFallback,
): Promise<SoundscapeDoc> {
  try {
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) {
      if (fallback) {
        const f = await fallback();
        console.warn('[soundscape] no ' + url + '; migrated from legacy states/cues');
        const m = migrateCues(f.placements, f.cues); // N10: cues → trigger clips
        return { version: SOUNDSCAPE_VERSION, placements: m.placements, cues: m.cues };
      }
      console.warn('[soundscape] no ' + url + ' and no fallback; starting empty');
      return { version: SOUNDSCAPE_VERSION, placements: [], cues: [] };
    }
    // Read raw text (not res.json()) so we can hash the exact bytes on disk for
    // the freshness guard, then parse from the same text.
    const text = await res.text();
    const data = JSON.parse(text);
    const rawP: unknown[] = Array.isArray(data?.placements) ? data.placements : [];
    const rawC: unknown[] = Array.isArray(data?.cues) ? data.cues : [];
    // Partition into valid (loaded) + invalid (quarantined verbatim). Invalid
    // entries are warned with id/index and re-emitted on save, never silently
    // deleted (S2) — a typo or a forward-compat field survives until fixed.
    const placements: SoundPlacement[] = [];
    const droppedP: unknown[] = [];
    rawP.forEach((r, i) => {
      const v = validatePlacement(r);
      if (v) placements.push(v);
      else { droppedP.push(r); console.warn(`[soundscape] placement #${i} invalid — kept verbatim, not loaded (id: ${JSON.stringify(entryId(r))})`); }
    });
    const cues: AudioCue[] = [];
    const droppedC: unknown[] = [];
    rawC.forEach((r, i) => {
      const v = validateCue(r);
      if (v) cues.push(v);
      else { droppedC.push(r); console.warn(`[soundscape] cue #${i} invalid — kept verbatim, not loaded (id: ${JSON.stringify(entryId(r))})`); }
    });
    const dropped = droppedP.length || droppedC.length ? { placements: droppedP, cues: droppedC } : undefined;
    // N10 — fold valid cues into placements as trigger clips (cues[] retired).
    const merged = migrateCues(dedupeById(placements, 'placement'), dedupeById(cues, 'cue'));
    return {
      version: typeof data?.version === 'number' ? data.version : SOUNDSCAPE_VERSION,
      placements: merged.placements,
      cues: merged.cues,
      baseHash: await sha256Hex(text),
      dropped,
    };
  } catch (e) {
    if (fallback) {
      try {
        const f = await fallback();
        console.warn('[soundscape] load failed; migrated from legacy states/cues', e);
        const m = migrateCues(f.placements, f.cues); // N10: cues → trigger clips
        return { version: SOUNDSCAPE_VERSION, placements: m.placements, cues: m.cues };
      } catch { /* fall through to empty */ }
    }
    console.warn('[soundscape] load failed; starting empty', e);
    return { version: SOUNDSCAPE_VERSION, placements: [], cues: [] };
  }
}
